/**
 * Offline-first TanStack Query setup: long-lived cache persisted to IndexedDB (idb-keyval), paused mutations
 * persisted as an outbox and resumed after restore. Startup restores from disk before the UI mounts (local only —
 * never waits for the network).
 */
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/query-core";
import { persistQueryClientRestore, persistQueryClientSubscribe, type Persister } from "@tanstack/react-query-persist-client";
import { errorKind, type ApiClient } from "@bundle/core";
import { del, get, set } from "idb-keyval";
import { APP_VERSION, STORAGE_NAMESPACE } from "../env.ts";
import { registerMutations } from "./mutations.ts";
import { retryDelay, shouldRetry } from "./retry.ts";

export const DAY = 86_400_000;
export const CACHE_MAX_AGE = 7 * DAY;

/** `onAuthError` runs when any request is rejected with 401 (the session renews or signs out). */
export function createQueryClient(onAuthError: () => void = () => {}): QueryClient {
  const onError = (e: unknown) => {
    if (errorKind(e) === "auth") onAuthError();
  };
  return new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      queries: {
        networkMode: "offlineFirst",
        gcTime: CACHE_MAX_AGE,
        staleTime: Infinity,
        retry: shouldRetry,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      mutations: {
        networkMode: "offlineFirst",
        gcTime: CACHE_MAX_AGE,
        retry: shouldRetry,
        retryDelay,
      },
    },
  });
}

export function createPersister(): Persister {
  return createAsyncStoragePersister({
    storage: {
      getItem: async (key) => (await get<string>(key)) ?? null,
      setItem: (key, value) => set(key, value),
      removeItem: (key) => del(key),
    },
    key: `${STORAGE_NAMESPACE}.query-cache`,
    throttleTime: 800,
  });
}

const persistOptions = (client: QueryClient, persister: Persister) => ({
  queryClient: client,
  persister,
  maxAge: CACHE_MAX_AGE,
  buster: APP_VERSION,
  dehydrateOptions: {
    // Workspace data only; failed/pending queries are refetched anyway.
    shouldDehydrateQuery: (q: { queryKey: readonly unknown[]; state: { status: string } }) => q.queryKey[0] === "ws" && q.state.status === "success",
  },
});

/**
 * Restores the persisted cache + outbox (local IndexedDB read), then keeps persisting changes and resumes
 * paused mutations. Returns an unsubscribe function.
 */
export async function restoreAndPersist(client: QueryClient, persister: Persister, api: ApiClient): Promise<() => void> {
  registerMutations(client, api);
  try {
    await persistQueryClientRestore(persistOptions(client, persister));
  } catch {
    // Corrupt or incompatible cache: start fresh.
    await persister.removeClient();
  }
  const unsubscribe = persistQueryClientSubscribe(persistOptions(client, persister));
  // mount() wires onlineManager/focusManager: paused (offline) mutations resume when the network returns.
  client.mount();
  void client.resumePausedMutations();
  return () => {
    unsubscribe();
    client.unmount();
  };
}
